/**
 * One control of a compliance assessment (QualityAssessments): its article, title, priority and status; each automatic
 * check with its state, detail and evidence links (Code at the line, Data on a store, Restore points, Traces, a Quality
 * check, a release); each question with its answer, and for editors a form to answer (yes / no / n/a, a choice, a date,
 * text, a note), saved with "Save answers". "Fix with AI" (editors, with a chat) asks the project chat to fix the gap.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Sparkle } from '@phosphor-icons/react';
import { Pill } from './QualityCommon';
import {
  PRIORITY, answerText, changedAnswers, controlStatus, evidenceWhere, itemState,
} from './assessmentModel';
import { relativeTime } from './qualityModel';

function EvidenceLink({ item, onOpen }) {
  const where = evidenceWhere(item);
  const target = onOpen(item);
  return (
    <li>
      {target
        ? <button className="quality-link" onClick={target} title={`Open ${where}`} type="button">{where}</button>
        : <span className="quality-mono">{where}</span>}
      {item.label && !/^Open /.test(item.label) && <span className="quality-muted"> · {item.label}</span>}
    </li>
  );
}

EvidenceLink.propTypes = { item: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

function CheckRow({ item, onOpenEvidence }) {
  const st = itemState(item.state);
  return (
    <li className={`quality-assess-item is-${item.state}`} data-testid={`assess-check-${item.id}`}>
      <Pill className="is-xs" tone={st.tone}>{st.label}</Pill>
      <div className="quality-grow">
        <strong>{item.label}</strong>
        <span className="quality-small quality-muted"> {item.detail}</span>
        {item.state === 'fail' && item.fix && <span className="quality-small quality-assess-fix">To fix: {item.fix}</span>}
        {item.evidence?.length > 0 && (
          <ul aria-label={`Evidence for ${item.label}`} className="quality-assess-evidence quality-small">
            {item.evidence.slice(0, 6).map((e, i) => <EvidenceLink item={e} key={`${e.kind}-${e.path || e.store || e.check || e.id || ''}-${e.line || i}`} onOpen={onOpenEvidence} />)}
          </ul>
        )}
      </div>
    </li>
  );
}

CheckRow.propTypes = { item: PropTypes.object.isRequired, onOpenEvidence: PropTypes.func.isRequired };

function AnswerInput({ q, value, onChange, disabled }) {
  const id = `assess-q-${q.id}`;
  if (q.questionKind === 'yesno') {
    const options = [['yes', 'Yes'], ['no', 'No'], ...(q.allowNa ? [['na', 'Not applicable']] : [])];
    return (
      <div aria-label={q.text} className="quality-chips" role="radiogroup">
        {options.map(([v, label]) => (
          <button aria-checked={value === v} className="quality-chip" disabled={disabled} key={v} onClick={() => onChange(value === v ? '' : v)} role="radio" type="button">{label}</button>
        ))}
      </div>
    );
  }
  if (q.questionKind === 'choice') {
    return (
      <select aria-label={q.text} className="quality-input is-sm" disabled={disabled} id={id} onChange={(e) => onChange(e.target.value)} value={value || ''}>
        <option value="">Choose…</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        {q.allowNa && <option value="na">Not applicable</option>}
      </select>
    );
  }
  return (
    <input
      aria-label={q.text}
      className="quality-input is-sm"
      disabled={disabled}
      id={id}
      onChange={(e) => onChange(e.target.value)}
      placeholder={q.questionKind === 'date' ? 'YYYY-MM-DD' : 'Your answer'}
      type={q.questionKind === 'date' ? 'date' : 'text'}
      value={value || ''}
    />
  );
}

AnswerInput.propTypes = { disabled: PropTypes.bool, onChange: PropTypes.func.isRequired, q: PropTypes.object.isRequired, value: PropTypes.string };

function QuestionRow({ q, canEdit, draft, setDraft, busy }) {
  const st = itemState(q.state);
  const d = draft[q.id] || { value: q.answer?.value || '', note: q.answer?.note || '' };
  const set = (patch) => setDraft({ ...draft, [q.id]: { ...d, ...patch } });
  return (
    <li className={`quality-assess-item is-${q.state}`} data-testid={`assess-question-${q.id}`}>
      <Pill className="is-xs" tone={st.tone}>{q.answer ? st.label : 'To answer'}</Pill>
      <div className="quality-grow">
        <strong>{q.text}</strong>
        {q.help && <span className="quality-small quality-muted"> {q.help}</span>}
        {canEdit ? (
          <div className="quality-assess-answer">
            <AnswerInput disabled={busy} onChange={(value) => set({ value })} q={q} value={d.value} />
            <input aria-label={`Note: ${q.text}`} className="quality-input is-sm quality-grow" disabled={busy} maxLength={500} onChange={(e) => set({ note: e.target.value })} placeholder="Note (optional): where the evidence is" type="text" value={d.note} />
          </div>
        ) : <span className="quality-small"> {answerText(q)}{q.answer?.note ? ` · ${q.answer.note}` : ''}</span>}
        {q.answer && <span className="quality-small quality-muted quality-assess-by">Answered by {q.answer.by || 'someone'}{q.answer.at ? `, ${relativeTime(Date.parse(q.answer.at))}` : ''}</span>}
      </div>
    </li>
  );
}

QuestionRow.propTypes = { busy: PropTypes.bool, canEdit: PropTypes.bool, draft: PropTypes.object.isRequired, q: PropTypes.object.isRequired, setDraft: PropTypes.func.isRequired };

export default function AssessmentControl({ control, canEdit, onSaveAnswers, onFix = null, onOpenEvidence }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const status = controlStatus(control.status);
  const changes = changedAnswers(control.questions, draft);
  const fixable = onFix && canEdit && ['gap', 'todo', 'partial'].includes(control.status) && (control.checks.some((c) => c.state === 'fail' || c.notRun) || control.status === 'gap');
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSaveAnswers(changes);
      setDraft({});
    } catch (e) {
      setError(e?.body?.error || e?.message || 'Could not save the answers');
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className={`quality-card quality-pad quality-assess-control is-${control.status}`} data-testid={`assess-control-${control.id}`}>
      <div className="quality-prov-line">
        <code className="quality-mono quality-comp-id">{control.ref}</code>
        <strong className="quality-grow">{control.title}</strong>
        <Pill className="is-xs" tone={control.priority === 'blocker' ? 'warn' : 'info'}>{PRIORITY[control.priority] || control.priority}</Pill>
        {control.manual && <Pill className="is-xs" title="Flow cannot check this automatically: the questions are the evidence" tone="info">manual</Pill>}
        <Pill className="is-xs" tone={status.tone}>{status.label}</Pill>
      </div>
      {control.why && <p className="quality-muted quality-small">{control.why}</p>}
      {control.checks.length > 0 && (
        <ul aria-label="Automatic checks" className="quality-assess-items">
          {control.checks.map((item) => <CheckRow item={item} key={item.id} onOpenEvidence={onOpenEvidence} />)}
        </ul>
      )}
      {control.questions.length > 0 && (
        <ul aria-label="Questions" className="quality-assess-items">
          {control.questions.map((q) => <QuestionRow busy={busy} canEdit={canEdit} draft={draft} key={q.id} q={q} setDraft={setDraft} />)}
        </ul>
      )}
      {error && <p className="quality-banner is-warn" role="alert">{error}</p>}
      {(fixable || (canEdit && changes.length > 0) || control.fix) && (
        <div className="quality-check-foot">
          <span className="quality-muted quality-small quality-grow">{control.fix ? `To fix: ${control.fix}` : ''}</span>
          {canEdit && changes.length > 0 && <button className="quality-btn is-sm" disabled={busy} onClick={save} type="button">{busy ? 'Saving…' : `Save ${changes.length === 1 ? 'answer' : 'answers'}`}</button>}
          {fixable && <button className="quality-btn is-sm is-primary" onClick={() => onFix(control)} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>}
        </div>
      )}
    </li>
  );
}

AssessmentControl.propTypes = {
  canEdit: PropTypes.bool,
  control: PropTypes.object.isRequired,
  // (control) => void: "Fix with AI" (the project chat).
  onFix: PropTypes.func,
  // (evidence) => (() => void) | null: what a click on an evidence link opens (null: plain text).
  onOpenEvidence: PropTypes.func.isRequired,
  // (answers) => Promise: saves `[{ question, value, note? }]`.
  onSaveAnswers: PropTypes.func.isRequired,
};
